import { useMutation } from '@apollo/client';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TinyBody, palettes, useFlashMessage } from '@indigo-ag/component-module';
import i18next from 'i18next';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { financingAgFinancing, toggleAgFinancingPriority } from 'features/brazil-grower/queries';
import { date, heapUtil } from 'shared/utils';
import { PriorityModal } from './PriorityModal';

interface Props {
  prioritySince: Date | null | undefined;
}

export const PriorityButton = ({ prioritySince }: Props) => {
  const { addFlashMessage } = useFlashMessage();
  const { id } = useParams<{ id: string }>();

  const [toggleAgFinancingPriorityFn, { loading }] = useMutation(toggleAgFinancingPriority, {
    awaitRefetchQueries: true
  });

  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);

  const updatePriority = async (newPriority: boolean, notes: string | null = null) => {
    try {
      await toggleAgFinancingPriorityFn({
        refetchQueries: [{ query: financingAgFinancing, variables: { agFinancingId: id } }],
        variables: { agFinancingId: id, isPriority: newPriority, notes }
      });
      addFlashMessage({
        content: i18next.t(newPriority ? 'markedAsPriority' : 'unMarkedAsPriority'),
        title: '',
        variant: 'success'
      });
      heapUtil.track('mark-as-priority', { newPriority, agFinancingId: id });
    } catch {
      addFlashMessage({
        content: i18next.t('error'),
        title: '',
        variant: 'error'
      });
    }
  };

  const isPriority = prioritySince !== null && prioritySince !== undefined;
  return (
    <>
      <div className="flex flex-column align-items-center">
        <Button
          busy={loading}
          onClick={() => (isPriority ? updatePriority(false) : setIsPriorityModalOpen(true))}
          size="small"
          variant="tertiary"
        >
          {i18next.t(isPriority ? 'unMarkAsPriority' : 'markAsPriority')}
        </Button>

        {isPriority && (
          <TinyBody>
            <FontAwesomeIcon icon={faStar} color={palettes.brand.red.base} />{' '}
            {date.formatDate(prioritySince, 'MMM DD, YYYY')}
          </TinyBody>
        )}
      </div>

      <PriorityModal
        closeModal={() => setIsPriorityModalOpen(false)}
        message={i18next.t('notesRequiredPriority')}
        notesRequired={true}
        onConfirmAction={updatePriority}
        open={isPriorityModalOpen}
        title={i18next.t('markAsPriority')}
      />
    </>
  );
};
